import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { extractFunctionErrorMessage } from '@/features/financials/hooks';
import { ticketKeys, useCreateTicket } from '@/features/tickets/hooks';
import { ticketRef } from '@/features/tickets/model';
import { formatDate } from '@/lib/formatters';
import type {
  CustomerFeedbackChannel,
  CustomerFeedbackTaskKind,
  Inserts,
  Tables,
  TicketPriority,
  TicketStatus,
  Updates,
} from '@/types/database';
import { FEEDBACK_CHANNEL_META, feedbackCustomerLabel } from './model';

/**
 * Hooks for Customer Feedback (migration 147).
 *
 * The browser talks to customer_feedback / customer_feedback_tasks
 * directly; RLS limits everything to people with the Sales section. The
 * AI pass goes through the `customer-feedback-extract` Edge Function so
 * the Anthropic key stays on the server; "Send request" reuses the
 * ticket hooks so a task becomes an ordinary ticket (T-0042) that the
 * receiving team works in their inbox.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CustomerFeedback = Tables<'customer_feedback'>;
export type CustomerFeedbackTask = Tables<'customer_feedback_tasks'>;

export interface FeedbackPerson {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

export interface FeedbackTaskWithContext extends CustomerFeedbackTask {
  ticket: {
    id: string;
    number: number;
    status: TicketStatus;
    title: string;
    assignee_id: string | null;
  } | null;
  suggested_category: { id: string; name: string; slug: string } | null;
}

export interface FeedbackWithContext extends CustomerFeedback {
  customer: { id: string; name: string; email: string | null } | null;
  author: FeedbackPerson | null;
  tasks: FeedbackTaskWithContext[];
}

const PERSON = 'id, full_name, email, avatar_url';

const FEEDBACK_SELECT =
  '*, ' +
  'customer:customer_id(id, name, email), ' +
  `author:created_by(${PERSON}), ` +
  'tasks:customer_feedback_tasks(' +
  '*, ' +
  'ticket:ticket_id(id, number, status, title, assignee_id), ' +
  'suggested_category:suggested_category_id(id, name, slug)' +
  ')';

export const customerFeedbackKeys = {
  all: ['customer-feedback'] as const,
  list: () => [...customerFeedbackKeys.all, 'list'] as const,
  customers: () => [...customerFeedbackKeys.all, 'customers'] as const,
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The whole log, newest interaction first, with each entry's tasks
 * embedded. Volume is small (a few entries a day) so one query keeps
 * the counts in the header exact and the feed filterable client-side.
 */
export function useCustomerFeedback() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: customerFeedbackKeys.list(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customer_feedback')
        .select(FEEDBACK_SELECT)
        .order('occurred_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      const rows = (data ?? []) as unknown as FeedbackWithContext[];
      for (const row of rows) {
        row.tasks = [...(row.tasks ?? [])].sort(
          (a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at),
        );
      }
      return rows;
    },
  });
}

/**
 * Catalog customers for the "who was this" picker. The financials
 * module has its own `useCustomers` gated on financials access; the
 * customers table itself is readable org-wide so Sales gets its own
 * lightweight read here.
 */
export function useFeedbackCustomers() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: customerFeedbackKeys.customers(),
    enabled: !!profile,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, email, is_active')
        .eq('is_active', true)
        .order('name', { ascending: true })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as Array<
        Pick<Tables<'customers'>, 'id' | 'name' | 'email' | 'is_active'>
      >;
    },
  });
}

// ---------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------

export interface SaveFeedbackInput {
  id?: string;
  customerId?: string | null;
  customerName?: string | null;
  channel: CustomerFeedbackChannel;
  /** ISO timestamp. */
  occurredAt?: string | null;
  interaction: string;
  repNotes?: string | null;
}

export function useSaveCustomerFeedback() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: SaveFeedbackInput) => {
      if (!profile) throw new Error('Not signed in.');
      const interaction = input.interaction.trim();
      if (!interaction) throw new Error('Paste the customer interaction first.');
      if (input.id) {
        const patch: Updates<'customer_feedback'> = {
          customer_id: input.customerId ?? null,
          customer_name: input.customerName?.trim() || null,
          channel: input.channel,
          interaction,
          rep_notes: input.repNotes?.trim() || null,
          ...(input.occurredAt ? { occurred_at: input.occurredAt } : {}),
        };
        const { data, error } = await supabase
          .from('customer_feedback')
          .update(patch)
          .eq('id', input.id)
          .select(FEEDBACK_SELECT)
          .single();
        if (error) throw error;
        return data as unknown as FeedbackWithContext;
      }
      const payload: Inserts<'customer_feedback'> = {
        organization_id: profile.organization_id,
        customer_id: input.customerId ?? null,
        customer_name: input.customerName?.trim() || null,
        channel: input.channel,
        interaction,
        rep_notes: input.repNotes?.trim() || null,
        created_by: profile.id,
        ...(input.occurredAt ? { occurred_at: input.occurredAt } : {}),
      };
      const { data, error } = await supabase
        .from('customer_feedback')
        .insert(payload)
        .select(FEEDBACK_SELECT)
        .single();
      if (error) throw error;
      return data as unknown as FeedbackWithContext;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerFeedbackKeys.list() });
    },
  });
}

export function useDeleteCustomerFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('customer_feedback').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerFeedbackKeys.list() });
    },
  });
}

// ---------------------------------------------------------------------------
// AI extraction
// ---------------------------------------------------------------------------

export interface ExtractResult {
  feedback: Pick<
    CustomerFeedback,
    'id' | 'ai_summary' | 'ai_sentiment' | 'ai_status' | 'ai_model' | 'ai_processed_at'
  >;
  tasks: CustomerFeedbackTask[];
  model: string;
}

/**
 * Run (or re-run) the extraction for one entry. Flips the row to
 * `processing` optimistically so the card shows the spinner at once;
 * the function replaces the entry's *suggested* tasks and keeps the
 * ones already sent or dismissed.
 */
export function useProcessCustomerFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { feedbackId: string; model?: string }) => {
      const { data, error } = await supabase.functions.invoke<
        ({ ok: true } & ExtractResult) | { ok: false; error?: string }
      >('customer-feedback-extract', {
        body: { feedback_id: input.feedbackId, ...(input.model ? { model: input.model } : {}) },
      });
      if (error) throw new Error(await extractFunctionErrorMessage(error));
      if (!data || !data.ok) {
        throw new Error((data && 'error' in data && data.error) || 'Extraction failed');
      }
      return data as ExtractResult;
    },
    onMutate: async ({ feedbackId }) => {
      await qc.cancelQueries({ queryKey: customerFeedbackKeys.list() });
      const prev = qc.getQueryData<FeedbackWithContext[]>(customerFeedbackKeys.list());
      if (prev) {
        qc.setQueryData(
          customerFeedbackKeys.list(),
          prev.map((f) =>
            f.id === feedbackId ? { ...f, ai_status: 'processing', ai_error: null } : f,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(customerFeedbackKeys.list(), ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: customerFeedbackKeys.list() });
    },
  });
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export interface SaveFeedbackTaskInput {
  id?: string;
  feedbackId: string;
  title: string;
  details?: string | null;
  kind: CustomerFeedbackTaskKind;
  priority: TicketPriority;
  categoryId?: string | null;
}

/** Add a task by hand, or edit a suggested one before sending it. */
export function useSaveFeedbackTask() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: SaveFeedbackTaskInput) => {
      if (!profile) throw new Error('Not signed in.');
      const title = input.title.trim();
      if (!title) throw new Error('Give the task a short title.');
      if (input.id) {
        const patch: Updates<'customer_feedback_tasks'> = {
          title,
          details: input.details?.trim() || null,
          kind: input.kind,
          suggested_priority: input.priority,
          suggested_category_id: input.categoryId ?? null,
        };
        const { data, error } = await supabase
          .from('customer_feedback_tasks')
          .update(patch)
          .eq('id', input.id)
          .select('*')
          .single();
        if (error) throw error;
        return data as CustomerFeedbackTask;
      }
      const payload: Inserts<'customer_feedback_tasks'> = {
        feedback_id: input.feedbackId,
        title,
        details: input.details?.trim() || null,
        kind: input.kind,
        suggested_priority: input.priority,
        suggested_category_id: input.categoryId ?? null,
        source: 'manual',
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('customer_feedback_tasks')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as CustomerFeedbackTask;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerFeedbackKeys.list() });
    },
  });
}

/** Dismiss a suggestion the rep disagrees with, or bring it back. */
export function useSetFeedbackTaskStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status: 'suggested' | 'dismissed' }) => {
      const { error } = await supabase
        .from('customer_feedback_tasks')
        .update({ status: input.status })
        .eq('id', input.id);
      if (error) throw error;
    },
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: customerFeedbackKeys.list() });
      const prev = qc.getQueryData<FeedbackWithContext[]>(customerFeedbackKeys.list());
      if (prev) {
        qc.setQueryData(
          customerFeedbackKeys.list(),
          prev.map((f) => ({
            ...f,
            tasks: f.tasks.map((t) => (t.id === id ? { ...t, status } : t)),
          })),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(customerFeedbackKeys.list(), ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: customerFeedbackKeys.list() });
    },
  });
}

export function useDeleteFeedbackTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('customer_feedback_tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerFeedbackKeys.list() });
    },
  });
}

export interface SendTaskRequestInput {
  task: FeedbackTaskWithContext;
  entry: FeedbackWithContext;
  title: string;
  description: string;
  categoryId: string | null;
  priority: TicketPriority;
  assigneeId: string | null;
  neededBy: string | null;
}

/**
 * Build the ticket body for a task. Written for the person who picks
 * the ticket up — they have not read the conversation, so the customer,
 * channel, the rep's notes and the exact words are all included.
 */
export function buildTaskRequestDescription(
  task: FeedbackTaskWithContext,
  entry: FeedbackWithContext,
): string {
  const customer = feedbackCustomerLabel(entry);
  const channel = FEEDBACK_CHANNEL_META[entry.channel].label.toLowerCase();
  const rep = entry.author?.full_name?.trim() || entry.author?.email || 'the sales team';
  const parts: string[] = [
    `From customer feedback — ${customer} via ${channel} on ${formatDate(entry.occurred_at)}, logged by ${rep}.`,
  ];
  if (task.details) parts.push(task.details);
  if (task.evidence) parts.push(`Customer said: “${task.evidence}”`);
  if (entry.rep_notes) {
    const notes =
      entry.rep_notes.length > 600 ? `${entry.rep_notes.slice(0, 600)}…` : entry.rep_notes;
    parts.push(`Rep notes: ${notes}`);
  }
  return parts.join('\n\n');
}

/**
 * "Send request": file the task as a ticket and mark it sent. The rep
 * is the requester, so they follow the ticket's status from their
 * inbox; the task keeps the ticket id so the feed shows "Sent · T-0042".
 */
export function useSendFeedbackTaskRequest() {
  const qc = useQueryClient();
  const createTicket = useCreateTicket();
  return useMutation({
    mutationFn: async (input: SendTaskRequestInput) => {
      const { ticket } = await createTicket.mutateAsync({
        title: input.title,
        description: input.description,
        categoryId: input.categoryId,
        priority: input.priority,
        assigneeId: input.assigneeId,
        neededBy: input.neededBy,
      });
      const { error } = await supabase
        .from('customer_feedback_tasks')
        .update({
          status: 'sent',
          ticket_id: ticket.id,
          // Keep what the rep actually sent, in case they edited the suggestion.
          title: input.title.trim(),
          suggested_priority: input.priority,
          suggested_category_id: input.categoryId,
        })
        .eq('id', input.task.id);
      if (error) {
        // The ticket exists; surface a precise message rather than a bare failure.
        throw new Error(
          `${ticketRef(ticket.number)} was filed but the task could not be marked as sent: ${error.message}`,
        );
      }
      return ticket;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: customerFeedbackKeys.list() });
      void qc.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}
